import { toast } from 'sonner';

import { useCreateCompanyNoteMutation } from '@/modules/companies/mutations/useCreateCompanyNoteMutation';
import { useCompanyNotesQuery } from '@/modules/companies/queries/useCompanyNotesQuery';
import type { NoteFormData } from '@/modules/notes/components/NoteForm';
import type { CompanyNoteType } from '@/modules/notes/components/NoteForm';
import { NotesList } from '@/modules/notes/components/NotesList';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { CompanyNoteCard } from './CompanyNoteCard';

export const companyNoteTypeOptions: {
  value: CompanyNoteType;
  label: string;
}[] = [
  { value: 'General', label: 'General' },
  { value: 'Intake', label: 'Intake' },
];

interface CompanyNotesListProps {
  companyId: string;
}

export const CompanyNotesList = ({ companyId }: CompanyNotesListProps) => {
  const { data: notes, isLoading } = useCompanyNotesQuery(companyId);
  const createNoteMutation = useCreateCompanyNoteMutation();

  const handleAddNote = (data: NoteFormData) => {
    createNoteMutation.mutate(
      {
        companyId,
        body: {
          content: data.content,
          type: data.type as CompanyNoteType,
        },
      },
      {
        onSuccess: () => {
          toast.success('Note added successfully');
        },
        onError: (error) => {
          toast.error(`Failed to add note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  return (
    <NotesList
      notes={notes}
      isLoading={isLoading}
      onAddNote={handleAddNote}
      isCreating={createNoteMutation.isPending}
      title="Notes"
      description="Keep track of important information about this company"
      typeOptions={companyNoteTypeOptions}
      renderNoteCard={(note) => (
        <CompanyNoteCard note={note} companyId={companyId} />
      )}
    />
  );
};
